import React, { ReactElement, useMemo, useState } from 'react';
import AuthorizationTokenStorage from '../services/auth/AuthorizationTokenStorage';
import { actionCreators } from '../store/actions';
import ReduxActions from '../store/ReduxActions';
import AuthorizationManager, { LoginData } from '../services/auth/AuthorizationManager';
import { AxiosRequestConfig } from 'axios';
import { LoginResponseInterface } from '../services/exports/Interfaces';
import collect from 'collect.js';
import { useTranslation } from 'react-i18next';
import { Response } from '../services/exports/Types';
import { store } from '../store';
import { OrdersApiSliceTags, ordersApiSlice } from '../store/api-slices';
import useLoginBonusHandler from '../features/auth/hooks/bonus/useLoginBonusHandler';
import useNotification from '../hooks/utility/useNotification';
import { delay } from '../utils/loading';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import mixpanel from 'mixpanel-browser';
import { resolveMixpanelProject } from '../services/utilities/mixpanelProject';
import { PENDING_BONUS_KEY } from '../utils/analytics/dealEventProps';
import { profileOrderFields } from '../services/helpers/ProfileOrderFields';
import { SessionStorageReservedKey, removeSessionStorageKeyForAllCompanies } from '../hooks/global/useSessionStorage';

interface Props {
  children: ReactElement;
}

interface AuthContextInterface {
  isLoggedIn: boolean;
  isGuest: boolean;
  loading: boolean;
  login: (data: LoginData, config?: AxiosRequestConfig) => Promise<Response<LoginResponseInterface>>;
  logout: (withFeedback?: boolean) => void;
  onLogin: (data: LoginResponseInterface, resetOrder?: boolean) => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextInterface>(null);

export default function AuthProvider({ children }: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Providers:AuthProvider' });
  const [isLoggedIn, setIsLoggedIn] = useState(!!AuthorizationTokenStorage.getToken());
  const [loading, setLoading] = useState<boolean>(false);
  const loginBonusHandler = useLoginBonusHandler();
  const { notify } = useNotification();
  const hasMixpanel = useSelector(
    (state: StoreInterface) => !!resolveMixpanelProject(state.initialData.company?.service_provider),
  );
  const login = async (data: LoginData, config?: AxiosRequestConfig) => {
    setLoading(true);
    await delay();
    const result = await AuthorizationManager.postLogin(data, config);
    setLoading(false);
    if (result.success) {
      await onLogin(result.response.data);
      notify({
        message: t('feedback.login_success'),
        icon: '✅',
      });
    }

    return result;
  };

  const logout = async (withFeedback = true) => {
    AuthorizationTokenStorage.deleteToken();

    onLogout();
    withFeedback &&
      notify({
        message: t('feedback.logout_success'),
        icon: '✅',
      });
  };

  async function onLogin(data: LoginResponseInterface, resetOrder: boolean = true): Promise<void> {
    await AuthorizationTokenStorage.saveToken(data.auth_token);

    setIsLoggedIn(true);

    store.dispatch(ordersApiSlice.util.invalidateTags([OrdersApiSliceTags.Order]));
    ReduxActions.batchActions(
      collect([actionCreators.profile.setProfile(data.user)])
        .when(resetOrder, (collection) =>
          collection.push(actionCreators.order.updateOrder(profileOrderFields(data.user))),
        )
        .toArray(),
    );

    if (data.bonus) {
      // Stash the bonus kind so AnalyticsProvider (which lives below this provider
      // and thus has access to useAnalytics) can fire the offer_applied conversion.
      try {
        sessionStorage.setItem(PENDING_BONUS_KEY, data.bonus.kind);
      } catch {
        // sessionStorage unavailable — skip the conversion event, not the bonus.
      }

      loginBonusHandler(data.bonus);
    }
  }

  function onLogout(): void {
    setIsLoggedIn(false);
    // A logged-out tab must not retry the previous customer's failed Payment Element order.
    removeSessionStorageKeyForAllCompanies(SessionStorageReservedKey.PaymentElementRetryOrder);

    ReduxActions.batchActions([
      actionCreators.order.resetOrder(),
      actionCreators.profile.setProfile(null),
      actionCreators.initialData.reset(),
      actionCreators.paymentOptions.reset(),
    ]);

    // Mixpanel is only initialised after cookie consent; reset() on an uninitialised instance throws.
    hasMixpanel && mixpanel?.['__loaded'] && mixpanel.reset();
  }

  const context = useMemo(
    () => ({
      isLoggedIn,
      isGuest: !isLoggedIn,
      loading,
      login,
      logout,
      onLogin,
    }),
    [isLoggedIn, loading],
  );

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>;
}
